/**
 * `PinButton` — the on-row pin/unpin affordance for the Object Explorer tree.
 *
 * Behaviour:
 *  - Renders nothing for nodes that are not pinnable (columns, sub-folders, etc.).
 *  - When the node is NOT pinned: shows an outline pin (visible only on row hover
 *    via the parent row's `group-hover` class). Click pins.
 *  - When the node IS pinned: shows a filled pin that is ALWAYS visible (no
 *    hover requirement). Click unpins. Both the original row under "Databases"
 *    and the pinned copy at the top render the filled pin (they share the same
 *    `pinRefKey`).
 *  - `e.stopPropagation()` on click so the row's expand/select isn't triggered.
 *
 * `connectionId` is derived from the node's metadata. For ordinary database /
 * folder / pinned-wrapper nodes it lives on `metadata.connectionId`; for leaf
 * `DatabaseObjectTreeNode`s it lives on `metadata.objectDetails.connectionId`.
 *
 * For `pinned-*` wrapper nodes we read `node.pinRef` directly (since
 * `pinRefFromNode` only knows about the underlying types). Either way the
 * `pinRefKey` matches the on-disk ref, so both row instances render the same
 * pin state.
 *
 * R3-9 (showcase review round 3) — `pinned-workspace` rows (a pinned Schema
 * Compare / Data Compare / query / notebook / table view) were the one pinned
 * row type with NO on-row unpin affordance: `resolvePinRef` didn't list them
 * and `pinRefFromNode` returns null for them by design, so this component
 * rendered nothing and the only route to unpin was the right-click menu —
 * which is not something a user finds by looking. They now render the same
 * always-visible filled pin every other pinned row has. Their unpin routes
 * through `executePinnedWorkspaceContextMenuAction('unpin', …)` rather than
 * the store directly, so the on-row button and the context menu share one
 * implementation: same `feature:workspace_pin_unpin` analytics (which carry
 * `{ workspaceType, source }`, not the object-pin `{ kind, enabled }` shape)
 * and the same rejection toast for over-cap / I/O failures.
 */

import React from 'react';
import { Pin } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { usePinnedObjectsStore } from '../../../database-objects/store/pinnedObjectsStore';
import {
  isWorkspacePinRef,
  pinRefFromNode,
  pinRefKey,
} from '../../../database-objects/services/pinnedObjects/pinRef';
import { executePinnedWorkspaceContextMenuAction } from '../../../database-objects/services/pinnedObjects/pinnedWorkspaceContextMenuService';
import type { DatabaseExplorerNode } from '../../models/nodeTypes';
import type { PinnedObjectRef } from '../../../../types/electron';

/**
 * Resolve a `PinnedObjectRef` from any tree node, including the `pinned-*`
 * wrapper nodes which carry the ref directly on the node (`pinRef`) rather
 * than via `pinRefFromNode`'s type mappings.
 */
function resolvePinRef(node: DatabaseExplorerNode): PinnedObjectRef | null {
  if (
    node.type === 'pinned-database' ||
    node.type === 'pinned-schema' ||
    node.type === 'pinned-folder' ||
    node.type === 'pinned-object' ||
    node.type === 'pinned-workspace' ||
    node.type === 'pinned-storage'
  ) {
    return node.pinRef;
  }
  return pinRefFromNode(node);
}

/**
 * Pull the owning connection id out of a tree node's metadata. Returns null
 * when the node carries no recognisable connection id (in which case we render
 * nothing — there's no IPC target to pin against).
 */
function resolveConnectionId(node: DatabaseExplorerNode): string | null {
  const md = node.metadata as Record<string, unknown> | undefined;
  if (!md) return null;
  // `connection` node: id lives on metadata.id, not metadata.connectionId.
  // PinButton never renders on connections (resolvePinRef returns null), but
  // we keep the check tight just in case.
  const direct = md.connectionId;
  if (typeof direct === 'string' && direct.length > 0) return direct;
  // DatabaseObjectTreeNode wraps the connection id inside `objectDetails`.
  const od = md.objectDetails as { connectionId?: unknown } | undefined;
  if (od && typeof od.connectionId === 'string' && od.connectionId.length > 0) {
    return od.connectionId;
  }
  return null;
}

interface PinButtonProps {
  node: DatabaseExplorerNode;
  /**
   * Optional override — if provided, used directly. Otherwise derived from
   * the node's metadata. The override exists so tests (and any future caller
   * that already has the id in hand) don't have to re-construct full metadata.
   */
  connectionId?: string;
}

export function PinButton({ node, connectionId: connectionIdProp }: PinButtonProps) {
  const { track } = useAnalytics();
  const ref = resolvePinRef(node);
  const connectionId = connectionIdProp ?? resolveConnectionId(node);
  // Subscribe to this connection's pin list regardless of whether we'll render
  // — hooks must run unconditionally. Using `''` as a fallback connectionId is
  // safe: `byConnection['']` is always undefined so we just get the empty array.
  // `useShallow` keeps the selector stable across renders (the `?? []` fallback
  // returns a fresh array each call which would trip React's infinite-loop
  // guard otherwise).
  const pins: readonly PinnedObjectRef[] = usePinnedObjectsStore(
    useShallow(
      (s): readonly PinnedObjectRef[] =>
        s.byConnection[connectionId ?? '']?.pinnedObjects ?? [],
    ),
  );

  if (!ref || !connectionId) return null;

  const key = pinRefKey(ref);
  const isPinned = pins.some((r) => pinRefKey(r) === key);
  const workspaceRef = isWorkspacePinRef(ref) ? ref : null;

  // A workspace pin has no "pin me" direction from the tree — the row only
  // exists BECAUSE it is pinned, and it is created from a workspace toolbar,
  // never derived from a node (see `pinRefFromNode`). If the store says it
  // isn't pinned we're mid-unpin (the row is about to disappear), so render
  // nothing rather than an outline pin offering to re-create it.
  if (workspaceRef && !isPinned) return null;

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (workspaceRef) {
      // One implementation shared with the row's context menu: analytics fire
      // after the write succeeds, rejections become a toast. `onRename` is
      // unreachable for the 'unpin' action id.
      executePinnedWorkspaceContextMenuAction(
        'unpin',
        { connectionId, ref: workspaceRef },
        { onRename: () => {}, track },
      );
      return;
    }
    const store = usePinnedObjectsStore.getState();
    if (isPinned) {
      void store.unpin(connectionId, ref);
      track('feature:object_explorer_pin', { kind: ref.kind, enabled: false });
    } else {
      void store.pin(connectionId, ref);
      track('feature:object_explorer_pin', { kind: ref.kind, enabled: true });
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      title={isPinned ? 'Unpin' : 'Pin to top'}
      aria-label={isPinned ? 'Unpin' : 'Pin to top'}
      data-testid={isPinned ? 'tree-pin-filled' : 'tree-pin-outline'}
      className={[
        'ml-auto shrink-0 p-1 rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-[opacity,color,background-color,scale] active:scale-[0.96]',
        isPinned
          ? 'opacity-20 group-hover:opacity-100 focus-visible:opacity-100'
          : 'opacity-0 group-hover:opacity-40 focus-visible:opacity-100 hover:!opacity-100',
      ].join(' ')}
    >
      <Pin
        className="h-3.5 w-3.5"
        fill={isPinned ? 'currentColor' : 'none'}
      />
    </button>
  );
}
